// Configuration management for React frontend
// Centralizes environment variables and settings

export type AppMode = 'dev' | 'demo' | 'production';

// Extend window for runtime config injected by backend /config.js
declare global {
  interface Window {
    __APP_MODE__?: string;
    __SINGLE_TENANT__?: boolean;
    __GOOGLE_CLIENT_ID__?: string;
    __EMBEDDINGS_AVAILABLE__?: boolean;
    __UMAMI_WEBSITE_ID__?: string;
    __UMAMI_SCRIPT_SRC__?: string;
    __UMAMI_DOMAINS__?: string;
    __UMAMI_TAG__?: string;
  }
}

/**
 * Resolve AppMode from runtime config.
 *
 * Priority:
 *   1. window.__APP_MODE__ (set by backend /config.js)
 *   2. Vite env: VITE_AUTH_ENABLED=false -> dev
 *   3. Vite dev server -> dev
 *   4. Default: production
 */
function resolveAppMode(): AppMode {
  if (typeof window !== 'undefined' && window.__APP_MODE__) {
    const raw = window.__APP_MODE__.toLowerCase();
    if (raw === 'dev' || raw === 'demo' || raw === 'production') {
      return raw;
    }
  }
  if (import.meta.env.VITE_AUTH_ENABLED === 'false') {
    return 'dev';
  }
  if (import.meta.env.MODE === 'development') {
    return 'dev';
  }
  return 'production';
}

export interface AppConfig {
  // API Configuration
  apiBaseUrl: string;

  // Mode
  appMode: AppMode;

  // Authentication
  googleClientId: string;
  authEnabled: boolean;
  demoMode: boolean;
  singleTenant: boolean;

  // Environment
  isDevelopment: boolean;
  isProduction: boolean;
  isTesting: boolean;

  embeddingsAvailable: boolean;

  // Analytics
  umamiWebsiteId: string;
  umamiScriptSrc: string;
  umamiDomains: string;
  umamiTag: string;

  // Features
  enablePerformanceMonitoring: boolean;
  enableMemoryMonitoring: boolean;
  enableErrorReporting: boolean;

  // Timeouts and intervals
  wsReconnectInterval: number;
  wsMaxReconnectAttempts: number;
  queryStaleTime: number;
  queryRetryDelay: number;
}

// Extend window type to include runtime config
declare global {
  interface Window {
    API_BASE_URL?: string;
    WS_BASE_URL?: string;
  }
}

function normalizeApiPathname(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "");
  if (!trimmed) {
    return "/api";
  }
  if (/\/api(\/|$)/.test(trimmed)) {
    return trimmed;
  }
  return `${trimmed}/api`.replace(/\/+/g, "/");
}

function normalizeApiBaseUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  if (!trimmed.startsWith("http")) {
    const prefixed = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    const withoutTrailing = prefixed.replace(/\/+$/, "");
    if (!withoutTrailing) {
      return "/api";
    }
    if (/\/api(\/|$)/.test(withoutTrailing)) {
      return withoutTrailing;
    }
    return `${withoutTrailing}/api`.replace(/\/+/g, "/");
  }

  const url = new URL(trimmed);

  // Fail fast: reject Docker internal hostnames in browser
  if (typeof window !== "undefined" && url.hostname === "backend") {
    throw new Error(
      `FATAL: API_BASE_URL='${trimmed}' uses Docker hostname unreachable from browser. Set to '/api' instead.`
    );
  }

  const normalizedPath = normalizeApiPathname(url.pathname || "/");
  return `${url.origin}${normalizedPath}`;
}

// Load configuration from environment variables
function loadConfig(): AppConfig {
  const appMode = resolveAppMode();
  const isDevelopment = import.meta.env.MODE === 'development';
  const isProduction = import.meta.env.MODE === 'production';
  const isTesting = import.meta.env.MODE === 'test';
  const demoMode = appMode === 'demo';
  const runtimeUmamiWebsiteId = typeof window !== 'undefined' ? window.__UMAMI_WEBSITE_ID__ : undefined;
  const runtimeUmamiScriptSrc = typeof window !== 'undefined' ? window.__UMAMI_SCRIPT_SRC__ : undefined;
  const runtimeUmamiDomains = typeof window !== 'undefined' ? window.__UMAMI_DOMAINS__ : undefined;
  const runtimeUmamiTag = typeof window !== 'undefined' ? window.__UMAMI_TAG__ : undefined;

  // FAIL FAST: No fallbacks, no silent defaults
  // Production MUST have config.js loaded with API_BASE_URL
  let apiBaseUrl = typeof window !== 'undefined' && window.API_BASE_URL
    ? window.API_BASE_URL
    : (import.meta.env.VITE_API_BASE_URL || (isDevelopment ? '/api' : ''));

  // Single-domain architecture: each user subdomain (alice.longhouse.ai) serves
  // both frontend and API. Nginx proxies /api/* to the backend container.
  // No separate api-X.longhouse.ai subdomain needed.
  if (typeof window !== 'undefined' && isProduction) {
    const host = window.location.hostname.toLowerCase();
    // For any *.longhouse.ai domain, use same-origin /api
    if (host.endsWith('.longhouse.ai') || host === 'longhouse.ai') {
      apiBaseUrl = '/api';
    }
  }

  // When running behind the Vite proxy (e.g., Playwright E2E), force relative API paths
  // to avoid CORS and ensure X-Test-Worker routing works.
  if (import.meta.env.VITE_PROXY_TARGET && !isProduction) {
    apiBaseUrl = '/api';
  }

  if (isTesting) {
    if (!apiBaseUrl) {
      apiBaseUrl = 'http://127.0.0.1:47300';
    }
  }

  apiBaseUrl = normalizeApiBaseUrl(apiBaseUrl);

  // Validate required config in production. Prerender (web/scripts/prerender.mjs)
  // runs this module in Node with no window and no config.js; there is no
  // runtime config to be missing there.
  if (typeof window !== 'undefined' && isProduction && appMode === 'production') {
    // Only the API base is required. No client reads a WebSocket base from here, and a
    // self-hosted Runtime Host with auth on and no public URL serves it empty: requiring
    // it turned that server's every page into a blank screen.
    if (!apiBaseUrl) {
      throw new Error('FATAL: API_BASE_URL not configured! Add window.API_BASE_URL in config.js');
    }
  }

  return {
    // API Configuration
    apiBaseUrl,

    // Mode
    appMode,

    // Authentication
    googleClientId: (typeof window !== 'undefined' && window.__GOOGLE_CLIENT_ID__) || import.meta.env.VITE_GOOGLE_CLIENT_ID || "",
    authEnabled: appMode !== 'dev',
    demoMode,
    singleTenant: typeof window !== 'undefined' && window.__SINGLE_TENANT__ === true,

    embeddingsAvailable: typeof window !== 'undefined' && window.__EMBEDDINGS_AVAILABLE__ === true,

    // Analytics
    umamiWebsiteId: runtimeUmamiWebsiteId ?? import.meta.env.VITE_UMAMI_WEBSITE_ID ?? "",
    // No default host. A website id on its own must never point a self-hosted
    // instance's analytics at someone else's server — the URL is explicit or
    // analytics stays off.
    umamiScriptSrc:
      runtimeUmamiScriptSrc !== undefined
        ? runtimeUmamiScriptSrc
        : import.meta.env.VITE_UMAMI_SCRIPT_SRC || "",
    umamiDomains: runtimeUmamiDomains ?? import.meta.env.VITE_UMAMI_DOMAINS ?? "",
    umamiTag: runtimeUmamiTag ?? import.meta.env.VITE_UMAMI_TAG ?? "prod",

    // Environment
    isDevelopment,
    isProduction,
    isTesting,

    // Features
    enablePerformanceMonitoring: isDevelopment || import.meta.env.VITE_ENABLE_PERFORMANCE === 'true',
    enableMemoryMonitoring: isDevelopment || import.meta.env.VITE_ENABLE_MEMORY_MONITORING === 'true',
    enableErrorReporting: isProduction || import.meta.env.VITE_ENABLE_ERROR_REPORTING === 'true',

    // Timeouts and intervals (in milliseconds)
    wsReconnectInterval: parseInt(import.meta.env.VITE_WS_RECONNECT_INTERVAL || '5000'),
    wsMaxReconnectAttempts: parseInt(import.meta.env.VITE_WS_MAX_RECONNECT_ATTEMPTS || '5'),
    queryStaleTime: parseInt(import.meta.env.VITE_QUERY_STALE_TIME || '300000'), // 5 minutes
    queryRetryDelay: parseInt(import.meta.env.VITE_QUERY_RETRY_DELAY || '1000'),
  };
}

// Global configuration instance
export const config: AppConfig = loadConfig();

// Validation function to ensure required configuration is present. Authentication
// method availability is a backend contract (`/auth/methods`): self-hosted
// password instances and hosted SSO do not require a Google client id.
export function validateConfig(): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (config.appMode !== 'demo' && !config.apiBaseUrl) {
    errors.push('API base URL is required');
  }

  if (config.wsReconnectInterval < 1000) {
    errors.push('WebSocket reconnect interval should be at least 1000ms');
  }

  if (config.wsMaxReconnectAttempts < 1) {
    errors.push('WebSocket max reconnect attempts should be at least 1');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

// Development-only configuration validator
if (config.isDevelopment) {
  const validation = validateConfig();
  if (!validation.valid) {
    console.warn('⚠️  Configuration issues detected:', validation.errors);
  } else {
    console.log('✅ Configuration validation passed');
  }

  // Log current configuration in development
  console.log('🔧 App Configuration:', {
    environment: import.meta.env.MODE,
    appMode: config.appMode,
    apiBaseUrl: config.apiBaseUrl,
    authEnabled: config.authEnabled,
    performanceMonitoring: config.enablePerformanceMonitoring,
  });
}

export default config;
